/**
 * Canonical CSV serialization for audit / troubleshooting exports.
 * All troubleshooting CSVs should use these helpers so row width and escaping stay consistent.
 */

/** RFC 4180-style field escaping (comma, quote, CR/LF). */
export function escapeCsvField(val: unknown): string {
  if (val == null || val === '') return '';
  const s = String(val);
  if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

/** One CSV line from already-string cells (each cell escaped). */
export function joinCsvRow(fields: readonly string[]): string {
  return fields.map((f) => escapeCsvField(f)).join(',');
}

/**
 * Build a CSV document with a fixed header width; throws if any row length differs.
 */
export function buildCsvFromRows(header: string[], rows: string[][]): string {
  const w = header.length;
  for (let i = 0; i < rows.length; i++) {
    if (rows[i].length !== w) {
      throw new Error(
        `CSV row ${i} has ${rows[i].length} fields, expected ${w} (matches header)`
      );
    }
  }
  return [joinCsvRow(header), ...rows.map(joinCsvRow)].join('\r\n');
}

/**
 * Build a two-column metric CSV (metric, value) with escaping — common for pipeline diagnostics.
 */
export function buildMetricValueCsv(rows: string[][]): string {
  const header = ['metric', 'value'];
  for (let i = 0; i < rows.length; i++) {
    if (rows[i].length !== 2) {
      throw new Error(`metric CSV row ${i} expected 2 columns, got ${rows[i].length}`);
    }
  }
  return buildCsvFromRows(header, rows);
}
